import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Button, Card, Field, Input, Modal, PageHeader, Select, Spinner, Textarea } from "../components/ui";
import { cls, PROVIDER_LABELS, TEMPLATE_CATEGORY_LABELS } from "../lib/format";

const STEPS = [
  { id: 1, label: "Test Name" },
  { id: 2, label: "Test Type" },
  { id: 3, label: "Sending Method" },
  { id: 4, label: "Recipient Mailboxes" },
  { id: 5, label: "Advanced Options" },
];

type GroupNode = {
  id: string;
  name: string;
  kind: string;
  provider: string | null;
  accountType: string | null;
  children: GroupNode[];
};

type SeedAccount = {
  id: string;
  email: string;
  provider: string;
  accountType: string;
  region: string | null;
  groupId: string | null;
  status: string;
};

type SendingAccount = { id: string; name: string; email: string; provider: string; status: string };
type Template = { id: string; name: string; category: string; subject: string; bodyText: string; bodyHtml: string | null; fromName: string | null; replyTo: string | null };
type Test = { id: string; type: string; status: string };

function Checkbox({ checked, onChange, children, disabled }: { checked: boolean; onChange: (v: boolean) => void; children?: ReactNode; disabled?: boolean }) {
  return (
    <label className={cls("flex cursor-pointer items-center gap-2 text-sm", disabled && "cursor-not-allowed opacity-50")}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
      />
      {children}
    </label>
  );
}

export function TestWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [testId, setTestId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [testType, setTestType] = useState<"one_time" | "automated">("one_time");
  const [senderIds, setSenderIds] = useState<string[]>([]);
  const [selectedSeeds, setSelectedSeeds] = useState<Set<string>>(new Set());
  const [seedFilter, setSeedFilter] = useState("");
  const [spamChecks, setSpamChecks] = useState<string[]>(["spamassassin", "google", "microsoft"]);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [customCopy, setCustomCopy] = useState({ fromName: "", subject: "", bodyText: "", bodyHtml: "", replyTo: "" });
  const [textOnly, setTextOnly] = useState(false);
  const [disableOpenTracking, setDisableOpenTracking] = useState(true);
  const [disableClickTracking, setDisableClickTracking] = useState(true);
  const [deliveryMode, setDeliveryMode] = useState<"controlled_batch" | "one_by_one">("controlled_batch");
  const [sendDelay, setSendDelay] = useState(3);
  const [schedule, setSchedule] = useState({ frequency: "daily", intervalMinutes: 360, runTime: "09:00", timezone: "UTC", daysOfWeek: [1] as number[], startDate: "", endDate: "" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  const { data: senders } = useQuery({ queryKey: ["sending-accounts"], queryFn: () => api<{ items: SendingAccount[] }>("/sending-accounts?pageSize=200&status=active") });
  const { data: seedPage } = useQuery({ queryKey: ["seed-accounts-all"], queryFn: () => api<{ items: SeedAccount[] }>("/seed-accounts?pageSize=500&status=active") });
  const { data: groups } = useQuery({ queryKey: ["seed-groups"], queryFn: () => api<GroupNode[]>("/seed-groups") });
  const { data: templates } = useQuery({ queryKey: ["templates-active"], queryFn: () => api<{ items: Template[] }>("/templates?pageSize=200&activeOnly=true") });

  const seeds = seedPage?.items ?? [];
  const allSeeds = useMemo(() => seeds, [seeds]);

  const createTest = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api<Test>("/tests", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: (test) => setTestId(test.id),
  });
  const saveTest = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) => api(`/tests/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  });
  const launch = useMutation({
    mutationFn: (id: string) => api<any>(`/tests/${id}/launch`, { method: "POST", body: JSON.stringify({ schedule: testType === "automated" ? schedule : undefined }) }),
    onSuccess: (run) => navigate(`/tests/${run.testId}/runs/${run.id}`),
  });

  const seedAccountIds = useMemo(() => seeds.filter((s) => selectedSeeds.has(s.id)).map((s) => s.id), [seeds, selectedSeeds]);

  function idsUnderNode(node: GroupNode): string[] {
    const direct = seeds.filter((s) => s.groupId === node.id).map((s) => s.id);
    const children = node.children.flatMap((c) => idsUnderNode(c));
    return [...direct, ...children];
  }

  function nodeState(node: GroupNode): "all" | "some" | "none" {
    const ids = idsUnderNode(node);
    if (!ids.length) return "none";
    const selected = ids.filter((id) => selectedSeeds.has(id)).length;
    if (selected === ids.length) return "all";
    if (selected > 0) return "some";
    return "none";
  }

  function toggleNode(node: GroupNode) {
    const ids = idsUnderNode(node);
    const state = nodeState(node);
    setSelectedSeeds((prev) => {
      const next = new Set(prev);
      if (state === "all") ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }

  async function continueStep() {
    setError(null);
    setSaving(true);
    try {
      if (step === 1) {
        if (!name.trim()) throw new Error("Test name is required");
        const test = await createTest.mutateAsync({ name: name.trim(), type: testType });
        setTestId(test.id);
      } else if (testId) {
        const payload: Record<string, unknown> = {};
        if (step === 2) payload.type = testType;
        if (step === 3) payload.senderAccountIds = senderIds;
        if (step === 4) {
          payload.seedAccountIds = [...seedAccountIds];
          payload.spamChecks = spamChecks;
        }
        if (step === 5) {
          payload.templateId = templateId;
          payload.customCopy = customCopy;
          payload.textOnly = textOnly;
          payload.disableOpenTracking = disableOpenTracking;
          payload.disableClickTracking = disableClickTracking;
          payload.deliveryMode = deliveryMode;
          payload.sendDelaySeconds = sendDelay;
          if (testType === "automated") payload.schedule = schedule;
        }
        await saveTest.mutateAsync({ id: testId, payload });
      }
      if (step < 5) setStep(step + 1);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  async function launchTest() {
    setError(null);
    if (!testId) return;
    setSaving(true);
    try {
      // Step 5 has no "Continue" button — persist its configuration before launching.
      await saveTest.mutateAsync({
        id: testId,
        payload: {
          templateId,
          customCopy,
          textOnly,
          disableOpenTracking,
          disableClickTracking,
          deliveryMode,
          sendDelaySeconds: sendDelay,
          ...(testType === "automated" ? { schedule } : {}),
        },
      });
      await launch.mutateAsync(testId);
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  }

  const activeCopy = templateId ? templates?.items.find((t) => t.id === templateId) : null;
  const previewSubject = activeCopy?.subject ?? customCopy.subject;
  const previewText = activeCopy?.bodyText ?? customCopy.bodyText;
  const previewHtml = (activeCopy?.bodyHtml ?? customCopy.bodyHtml) || null;

  return (
    <div>
      <PageHeader title="Let's generate a new test" subtitle="Create an inbox placement test to measure where your email lands." actions={<Button variant="ghost" onClick={() => navigate("/tests")}>Cancel</Button>} />
      <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
        <aside>
          <Card className="p-0">
            <ol className="p-3">
              {STEPS.map((s) => (
                <li key={s.id} className="mb-1">
                  <button
                    onClick={() => s.id < step && setStep(s.id)}
                    className={cls(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                      step === s.id ? "bg-brand-50 font-semibold text-brand-700" : s.id < step ? "text-ink-600 hover:bg-ink-50" : "text-ink-400",
                    )}
                  >
                    <span
                      className={cls(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                        step === s.id ? "bg-brand-600 text-white" : s.id < step ? "bg-emerald-100 text-emerald-700" : "bg-ink-100 text-ink-500",
                      )}
                    >
                      {s.id < step ? "✓" : s.id}
                    </span>
                    {s.label}
                  </button>
                </li>
              ))}
            </ol>
          </Card>
        </aside>

        <div className="min-w-0">
          <Card>
            {step === 1 && (
              <div>
                <h2 className="text-lg font-bold text-ink-900">What is the name of your test?</h2>
                <p className="mt-1 text-sm text-ink-500">Pick a descriptive name so reports are easy to find later.</p>
                <div className="mt-6">
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Q4 Inbox Placement Test" className="text-base" autoFocus />
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="text-lg font-bold text-ink-900">Select the type of test</h2>
                <p className="mt-1 text-sm text-ink-500">What is the test purpose?</p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <button
                    onClick={() => setTestType("automated")}
                    className={cls("rounded-xl border-2 p-6 text-left transition-all", testType === "automated" ? "border-brand-500 bg-brand-50/50" : "border-ink-200 hover:border-ink-300")}
                  >
                    <div className="text-2xl">🔄</div>
                    <h3 className="mt-3 font-semibold text-ink-900">Automated</h3>
                    <ul className="mt-2 space-y-1 text-sm text-ink-500">
                      <li>• Set up recurring email tests</li>
                      <li>• Track performance over time</li>
                      <li>• Ideal for ongoing monitoring</li>
                    </ul>
                    <span className="mt-4 inline-block rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white">Select Automated Test</span>
                  </button>
                  <button
                    onClick={() => setTestType("one_time")}
                    className={cls("rounded-xl border-2 p-6 text-left transition-all", testType === "one_time" ? "border-brand-500 bg-brand-50/50" : "border-ink-200 hover:border-ink-300")}
                  >
                    <div className="text-2xl">⚡</div>
                    <h3 className="mt-3 font-semibold text-ink-900">One-time</h3>
                    <ul className="mt-2 space-y-1 text-sm text-ink-500">
                      <li>• Send a single test email for analysis</li>
                      <li>• Quick setup and immediate results</li>
                      <li>• Ideal for one-off deliverability tests</li>
                    </ul>
                    <span className="mt-4 inline-block rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white">Select One-Time Test</span>
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="text-lg font-bold text-ink-900">Sending method</h2>
                <p className="mt-1 text-sm text-ink-500">Choose the SMTP sending accounts used to send this test. Multiple accounts create separate placement data per sender.</p>
                <div className="mt-6 space-y-2">
                  {!senders?.items.length && <p className="rounded-lg bg-ink-50 p-4 text-sm text-ink-500">No active sending accounts. Add one in Accounts → Sending Accounts first.</p>}
                  {senders?.items.map((s) => (
                    <label key={s.id} className={cls("flex cursor-pointer items-center gap-3 rounded-xl border p-4", senderIds.includes(s.id) ? "border-brand-500 bg-brand-50/40" : "border-ink-200")}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-ink-300 text-brand-600"
                        checked={senderIds.includes(s.id)}
                        onChange={(e) => setSenderIds((prev) => (e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id)))}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink-800">{s.name}</div>
                        <div className="truncate text-xs text-ink-500">{s.email}</div>
                      </div>
                      <Badge tone="neutral">{PROVIDER_LABELS[s.provider] ?? s.provider}</Badge>
                    </label>
                  ))}
                </div>
                {senderIds.length > 0 && (
                  <p className="mt-4 rounded-lg bg-brand-50 px-4 py-2.5 text-xs font-medium text-brand-700">
                    {senderIds.length} sender account{senderIds.length > 1 ? "s" : ""} selected — each will be reported separately.
                  </p>
                )}
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="text-lg font-bold text-ink-900">Choose Your Email Provider(s)</h2>
                <p className="mt-1 text-sm text-ink-500">Select the seed accounts that will receive this test.</p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Input placeholder="Search mailboxes…" value={seedFilter} onChange={(e) => setSeedFilter(e.target.value)} className="max-w-xs" />
                  <Badge tone="brand">Selected Mailboxes: {selectedSeeds.size}</Badge>
                </div>
                <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_320px]">
                  <div className="max-h-[460px] overflow-auto rounded-xl border border-ink-200 p-4">
                    {seedFilter.trim() ? (
                      <div className="space-y-1">
                        {allSeeds
                          .filter((s) => s.email.toLowerCase().includes(seedFilter.trim().toLowerCase()))
                          .map((s) => (
                            <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-ink-50">
                              <input
                                type="checkbox"
                                checked={selectedSeeds.has(s.id)}
                                onChange={(e) =>
                                  setSelectedSeeds((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(s.id);
                                    else next.delete(s.id);
                                    return next;
                                  })
                                }
                                className="h-4 w-4 rounded border-ink-300 text-brand-600"
                              />
                              <span className="min-w-0 flex-1 truncate text-ink-700">{s.email}</span>
                              <Badge tone="neutral">{PROVIDER_LABELS[s.provider] ?? s.provider}</Badge>
                            </label>
                          ))}
                        {allSeeds.filter((s) => s.email.toLowerCase().includes(seedFilter.trim().toLowerCase())).length === 0 && (
                          <p className="py-4 text-center text-sm text-ink-400">No mailboxes match "{seedFilter}".</p>
                        )}
                      </div>
                    ) : (
                      <>
                        {groups?.map((region) => (
                          <div key={region.id} className="mb-2">
                            <Checkbox checked={nodeState(region) !== "none"} onChange={() => toggleNode(region)}>
                              <span className="font-semibold text-ink-800">{region.name}</span>
                              {nodeState(region) === "some" && <span className="text-xs text-ink-400">(partial)</span>}
                            </Checkbox>
                            <div className="ml-6 mt-1 space-y-1 border-l border-ink-100 pl-3">
                              {region.children.map((clsNode) => (
                                <div key={clsNode.id}>
                                  <Checkbox checked={nodeState(clsNode) !== "none"} onChange={() => toggleNode(clsNode)}>
                                    <span className="text-sm text-ink-700">{clsNode.name}</span>
                                    {nodeState(clsNode) === "some" && <span className="text-xs text-ink-400">(partial)</span>}
                                  </Checkbox>
                                  <div className="ml-6 mt-1 space-y-1 border-l border-ink-100 pl-3">
                                    {clsNode.children.map((provider) => (
                                      <Checkbox key={provider.id} checked={nodeState(provider) !== "none"} onChange={() => toggleNode(provider)}>
                                        <span className="text-sm text-ink-600">{provider.name}</span>
                                        <span className="text-xs text-ink-400">({idsUnderNode(provider).length})</span>
                                      </Checkbox>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        {allSeeds.some((s) => !s.groupId) && (
                          <div className="mt-4 border-t border-ink-100 pt-3">
                            <Checkbox
                              checked={allSeeds.filter((s) => !s.groupId).every((s) => selectedSeeds.has(s.id)) && allSeeds.some((s) => !s.groupId && selectedSeeds.has(s.id))}
                              onChange={(checked) =>
                                setSelectedSeeds((prev) => {
                                  const next = new Set(prev);
                                  const ungrouped = allSeeds.filter((s) => !s.groupId);
                                  if (checked) ungrouped.forEach((s) => next.add(s.id));
                                  else ungrouped.forEach((s) => next.delete(s.id));
                                  return next;
                                })
                              }
                            >
                              <span className="font-semibold text-ink-800">Ungrouped accounts</span>
                              <span className="text-xs text-ink-400">({allSeeds.filter((s) => !s.groupId).length})</span>
                            </Checkbox>
                            <div className="ml-6 mt-1 max-h-44 space-y-1 overflow-auto border-l border-ink-100 pl-3">
                              {allSeeds
                                .filter((s) => !s.groupId)
                                .map((s) => (
                                  <Checkbox key={s.id} checked={selectedSeeds.has(s.id)} onChange={(v) => setSelectedSeeds((prev) => { const next = new Set(prev); if (v) next.add(s.id); else next.delete(s.id); return next; })}>
                                    <span className="min-w-0 flex-1 truncate text-sm text-ink-600">{s.email}</span>
                                  </Checkbox>
                                ))}
                            </div>
                          </div>
                        )}
                        {!groups?.length && !allSeeds.length && <p className="py-6 text-center text-sm text-ink-400">No seed accounts found. Add active seed accounts first.</p>}
                      </>
                    )}
                  </div>
                  <div className="max-h-[460px] overflow-auto rounded-xl border border-ink-200 p-4">
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">Selected accounts</h3>
                    {allSeeds.filter((s) => selectedSeeds.has(s.id)).length === 0 && <p className="text-sm text-ink-400">Nothing selected yet.</p>}
                    {allSeeds
                      .filter((s) => selectedSeeds.has(s.id))
                      .slice(0, 80)
                      .map((s) => (
                        <div key={s.id} className="flex items-center justify-between border-b border-ink-50 py-1.5 text-xs">
                          <span className="truncate text-ink-700">{s.email}</span>
                          <Badge tone="neutral">{PROVIDER_LABELS[s.provider] ?? s.provider}</Badge>
                        </div>
                      ))}
                    {selectedSeeds.size > 80 && <p className="mt-2 text-xs text-ink-400">+{selectedSeeds.size - 80} more</p>}
                  </div>
                </div>
                <div className="mt-6">
                  <h3 className="mb-3 text-sm font-semibold text-ink-800">Spam Filters to Check</h3>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { id: "spamassassin", label: "SpamAssassin" },
                      { id: "google", label: "Google Spam Analysis" },
                      { id: "microsoft", label: "Microsoft Spam Analysis" },
                    ].map((f) => (
                      <Checkbox key={f.id} checked={spamChecks.includes(f.id)} onChange={(v) => setSpamChecks((prev) => (v ? [...prev, f.id] : prev.filter((x) => x !== f.id)))}>
                        {f.label}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-7">
                <section>
                  <h3 className="text-sm font-semibold text-ink-800">ACCOUNTS TO USE</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {senders?.items.filter((s) => senderIds.includes(s.id)).map((s) => (
                      <Badge key={s.id} tone="brand">{s.name}</Badge>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-sm font-semibold text-ink-800">CAMPAIGN / TEMPLATE TO USE</h3>
                  <div className="mt-3 grid gap-4 lg:grid-cols-2">
                    <Field label="Existing template">
                      <Select
                        value={templateId ?? ""}
                        onChange={(e) => {
                          const id = e.target.value || null;
                          setTemplateId(id);
                          const t = templates?.items.find((x) => x.id === id);
                          if (t) {
                            setCustomCopy({
                              fromName: t.fromName ?? "",
                              subject: t.subject,
                              bodyText: t.bodyText,
                              bodyHtml: t.bodyHtml ?? "",
                              replyTo: t.replyTo ?? "",
                            });
                          }
                        }}
                      >
                        <option value="">Write new copy…</option>
                        {templates?.items.map((t) => (
                          <option key={t.id} value={t.id}>{t.name} — {TEMPLATE_CATEGORY_LABELS[t.category] ?? t.category}</option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Reply-To">
                      <Input value={customCopy.replyTo ?? ""} onChange={(e) => setCustomCopy({ ...customCopy, replyTo: e.target.value })} placeholder="reply@example.com" />
                    </Field>
                  </div>
                  <div className="mt-4 space-y-4">
                    <Field label="From Name">
                      <Input value={customCopy.fromName} onChange={(e) => setCustomCopy({ ...customCopy, fromName: e.target.value })} placeholder="Deliverability Team" />
                    </Field>
                    <Field label="Subject">
                      <Input value={customCopy.subject} onChange={(e) => setCustomCopy({ ...customCopy, subject: e.target.value })} placeholder="Q4 product update" />
                    </Field>
                    <Field label="Email Body (text)">
                      <Textarea rows={5} value={customCopy.bodyText} onChange={(e) => setCustomCopy({ ...customCopy, bodyText: e.target.value })} placeholder="Hi {{firstName}},…" />
                    </Field>
                    <Field label="Email Body (HTML)" hint="Optional — leave empty for text-only">
                      <Textarea rows={6} value={customCopy.bodyHtml} onChange={(e) => setCustomCopy({ ...customCopy, bodyHtml: e.target.value })} placeholder="<p>Hi {{firstName}},</p>…" className="font-mono text-xs" />
                    </Field>
                  </div>
                </section>

                <section className="flex items-center justify-between rounded-xl border border-ink-200 bg-ink-50/50 px-5 py-4">
                  <div>
                    <h3 className="text-sm font-semibold text-ink-800">TEST EMAIL COPY</h3>
                    <p className="text-xs text-ink-500">Preview the rendered subject and body</p>
                  </div>
                  <Button variant="secondary" onClick={() => setPreviewOpen(true)}>View Copy</Button>
                </section>

                <section>
                  <h3 className="text-sm font-semibold text-ink-800">DELIVERY OPTIMIZATION</h3>
                  <div className="mt-3 space-y-2">
                    <Checkbox checked={textOnly} onChange={setTextOnly}>Send emails as text-only (no HTML)</Checkbox>
                    <Checkbox checked={disableOpenTracking} onChange={setDisableOpenTracking}>Disable open tracking</Checkbox>
                    <Checkbox checked={disableClickTracking} onChange={setDisableClickTracking}>Disable click tracking</Checkbox>
                  </div>
                  <p className="mt-2 text-xs text-ink-400">Tracking is disabled by default for deliverability testing so it does not influence placement.</p>
                </section>

                <section>
                  <h3 className="text-sm font-semibold text-ink-800">DELIVERY MODE</h3>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className={cls("cursor-pointer rounded-xl border p-4", deliveryMode === "controlled_batch" ? "border-brand-500 bg-brand-50/40" : "border-ink-200")}>
                      <input type="radio" className="sr-only" checked={deliveryMode === "controlled_batch"} onChange={() => setDeliveryMode("controlled_batch")} />
                      <div className="text-sm font-medium text-ink-800">Controlled batch</div>
                      <p className="mt-1 text-xs text-ink-500">Send messages sequentially without added delay between recipients.</p>
                    </label>
                    <label className={cls("cursor-pointer rounded-xl border p-4", deliveryMode === "one_by_one" ? "border-brand-500 bg-brand-50/40" : "border-ink-200")}>
                      <input type="radio" className="sr-only" checked={deliveryMode === "one_by_one"} onChange={() => setDeliveryMode("one_by_one")} />
                      <div className="text-sm font-medium text-ink-800">One by one</div>
                      <p className="mt-1 text-xs text-ink-500">Introduce a delay between each recipient (1–10 seconds).</p>
                      {deliveryMode === "one_by_one" && (
                        <div className="mt-3 flex items-center gap-2">
                          <Input type="number" min={1} max={10} value={sendDelay} onChange={(e) => setSendDelay(Number(e.target.value))} className="w-24" />
                          <span className="text-xs text-ink-500">seconds between recipients</span>
                        </div>
                      )}
                    </label>
                  </div>
                </section>

                {testType === "automated" && (
                  <section>
                    <h3 className="text-sm font-semibold text-ink-800">AUTOMATED TEST OPTIONS</h3>
                    <div className="mt-3 grid gap-4 sm:grid-cols-3">
                      <Field label="Frequency">
                        <Select value={schedule.frequency} onChange={(e) => setSchedule({ ...schedule, frequency: e.target.value })}>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="custom">Custom (every N hours)</option>
                        </Select>
                      </Field>
                      <Field label="Time">
                        <Input type="time" value={schedule.runTime} onChange={(e) => setSchedule({ ...schedule, runTime: e.target.value })} />
                      </Field>
                      <Field label="Timezone">
                        <Input value={schedule.timezone} onChange={(e) => setSchedule({ ...schedule, timezone: e.target.value })} placeholder="UTC" />
                      </Field>
                    </div>
                    {schedule.frequency === "weekly" && (
                      <div className="mt-4">
                        <Field label="Days of week">
                          <div className="flex gap-2">
                            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d, i) => (
                              <button
                                key={d}
                                type="button"
                                onClick={() => setSchedule({ ...schedule, daysOfWeek: schedule.daysOfWeek.includes(i) ? schedule.daysOfWeek.filter((x) => x !== i) : [...schedule.daysOfWeek, i] })}
                                className={cls("h-9 w-9 rounded-lg text-xs font-medium", schedule.daysOfWeek.includes(i) ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-600")}
                              >
                                {d}
                              </button>
                            ))}
                          </div>
                        </Field>
                      </div>
                    )}
                    {schedule.frequency === "custom" && (
                      <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        <Field label="Interval (minutes)" hint="Minimum 60 minutes to prevent abuse">
                          <Input type="number" min={60} step={60} value={schedule.intervalMinutes} onChange={(e) => setSchedule({ ...schedule, intervalMinutes: Number(e.target.value) })} />
                        </Field>
                      </div>
                    )}
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <Field label="Start date">
                        <Input type="date" value={schedule.startDate} onChange={(e) => setSchedule({ ...schedule, startDate: e.target.value })} />
                      </Field>
                      <Field label="End date (optional)">
                        <Input type="date" value={schedule.endDate} onChange={(e) => setSchedule({ ...schedule, endDate: e.target.value })} />
                      </Field>
                    </div>
                  </section>
                )}
              </div>
            )}

            {error ? (
              <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error instanceof Error ? error.message : String(error)}
              </div>
            ) : null}

            <div className="mt-8 flex items-center justify-between border-t border-ink-100 pt-5">
              <Button variant="ghost" onClick={() => (step > 1 ? setStep(step - 1) : navigate("/tests"))}>
                {step > 1 ? "← Back" : "Cancel"}
              </Button>
              {step < 5 ? (
                <Button onClick={continueStep} disabled={saving}>
                  {saving ? "Saving…" : "Continue →"}
                </Button>
              ) : (
                <Button onClick={launchTest} disabled={saving} size="lg">
                  {saving ? "Launching…" : "🚀 Launch Test"}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Test Email Copy" wide>
        <div className="space-y-4">
          <div>
            <div className="text-xs font-medium uppercase text-ink-400">Subject</div>
            <div className="mt-1 rounded-lg bg-ink-50 px-3 py-2 text-sm font-medium text-ink-800">{previewSubject || "—"}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase text-ink-400">Plain text</div>
            <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded-lg bg-ink-50 px-3 py-2 text-xs text-ink-700">{previewText || "—"}</pre>
          </div>
          {previewHtml && (
            <div>
              <div className="text-xs font-medium uppercase text-ink-400">HTML preview</div>
              <div className="mt-1 max-h-72 overflow-auto rounded-lg border border-ink-200 p-4" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
